<template>
    <div id="calendar">
        <div id="calendars">
            <configuration-section
                :title="$t('calendar.manage_calendar')"
                :description="$t('calendar.manage_calendar_description')"
            >
                <loading
                    v-if="loading"
                    align="left"
                    class="pt-24 pl-16 justify-start"
                    size="medium"
                />
                <div id="cronofy-calendar-sync"></div>

                <template v-if="!loading">
                    <simple-button
                        v-if="hasCalendar"
                        class="mt-16 mb-48 whitespace-nowrap"
                        look="secondary"
                        @click="selectCalendar(true)"
                    >
                        {{ $t('calendar.choose_active_calendar') }}
                    </simple-button>

                    <manage-calendar-availability/>
                </template>
            </configuration-section>

            <popup-modal
                v-if="chooseCal"
                :close-on-click="!!user.primary_calendar_id"
                :has-close-button="!!user.primary_calendar_id"
                custom-classes="w-768" @close="chooseCal = false"
            >
                <div v-if="syncingCalendars">
                    <loading class="mb-20"/>
                    <p class="text-primary-40 text-center">{{ $t('general.please_wait') }}</p>
                </div>
                <div v-else>
                    <p class="font-semibold text-heading-1 text-center tex-gray-800 mb-32">
                        {{ $t('calendar.success') }}
                    </p>
                    <p class="font-semibold text-center tex-gray-800">
                        {{ $t('calendar.choose_calendar') }}
                    </p>
                    <div v-for="profile in user.calendar_profiles">
                        <p class="font-semibold bg-primary-5 rounded-t p-12 mt-16 flex items-center">
                            <span>
                                <svg-icon :type="profile.icon" class="max-h-20 max-w-20 mr-8"/>
                            </span>
                            <span>{{ profile.profile_name }}</span>
                        </p>
                        <div
                            v-for="calendar in profile.calendars"
                            :class="primaryCalendarId === calendar.id ? 'border-secondary font-semibold' : 'border-transparent'"
                            class="hover:bg-primary-5 p-12 cursor-pointer flex justify-between items-center border transition-all"
                            @click="primaryCalendarId = calendar.id"
                        >
                            <p>{{ calendar.calendar_name }}</p>
                            <template v-if="calendar.valid">
                                <checkbox
                                    :checked="primaryCalendarId === calendar.id"
                                    @change="$event ? primaryCalendarId = calendar.id : null"
                                />
                            </template>
                            <template v-else>
                                <tooltip :content="$t('calendar.no_access_appointments')">
                                    <tag display="primary" disabled>
                                        {{ $t('permissions.options.no_access') }}
                                    </tag>
                                </tooltip>
                            </template>
                        </div>
                    </div>

                    <checkbox v-model="autosync" :value="user.cronofy_auto_sync" class="flex my-24">
                        {{ $t('calendar.auto_sync') }}
                    </checkbox>

                    <simple-button
                        v-if="primaryCalendarId"
                        class="ms-auto"
                        display="secondary"
                        look="primary"
                        @click="savePrimaryCalendar"
                    >
                        {{ $t('general.save') }}
                    </simple-button>
                </div>
            </popup-modal>
        </div>
    </div>
</template>

<script>
import ConfigurationSection from './ConfigurationSection.vue';
import * as calendar from '../services/CalendarService';
import * as users from '../services/UserService';
import {normalizeJson} from '../../shared/helpers';
import CalendarProfile from '../models/CalendarProfile';
import User from '../models/User';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import Loading from './ui/Loading.vue';
import PopupModal from './ui/PopupModal.vue';
import Checkbox from './forms/Checkbox.vue';
import SimpleButton from './ui/SimpleButton.vue';
import Tooltip from './navigation/Tooltip.vue';
import SvgIcon from './ui/SvgIcon.vue';
import CalendarSync from './ui/CalendarSync.vue';
import ManageCalendarAvailability from "./ManageCalendarAvailability.vue";
import Tag from "./ui/Tag.vue";

export default {
    components: {
        Tag,
        ManageCalendarAvailability,
        CalendarSync, SvgIcon, Tooltip, SimpleButton, Checkbox, PopupModal, Loading, ConfigurationSection},
    data() {
        return {
            loading: true,
            calendarElement: Object,
            chooseCal: this.$route.query.calendar === 'added',
            syncingCalendars: false,
            primaryCalendarId: null,
            autosync: true,
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
        user() {
            return useRepo(User).with('calendars').with('calendar_profiles').find(this.currentUserId);
        },
        hasCalendar() {
            return this.user.calendars && this.user.calendars.length;
        },
    },
    methods: {
        savePrimaryCalendar() {
            this.chooseCal = false;

            useRepo(User)
                .whereId(this.user.id)
                .update({ cronofy_auto_sync: this.autosync, primary_calendar_id: this.primaryCalendarId });

            users.update(this.user.id, {
                data: {attributes: {cronofy_auto_sync: this.autosync}},
            });

            calendar.setPrimaryCalendar(this.primaryCalendarId);

            if (this.$route.query.calendar) {
                let query = Object.assign({}, this.$route.query);
                delete query.calendar;
                this.$router.replace({query});
            }
        },
        selectCalendar(forced = false) {
            this.chooseCal = true;
            this.syncingCalendars = true;

            calendar.sync().then(data => {
                return useRepo(CalendarProfile).save(normalizeJson(data));
            }).then(() => {
                // Check if there are multiple calendars available
                const cals = this.user.calendars.filter(i => i.valid);


                if (cals.length === 1 && !forced) {
                    // They only have 1 valid calendar, don't make them choose. Just select this calendar as primary cal
                    this.primaryCalendarId = cals[0].id;
                    this.savePrimaryCalendar();
                } else {
                    this.primaryCalendarId = cals.find(x => x.calendar_primary).id;
                }

                this.syncingCalendars = false;
            });
        },
        getLocale() {
            return this.locale === "se" ? "sv" : this.locale;
        }
    },
    watch: {
        locale() {
            this.calendarElement.update({
                locale: this.getLocale()
            });
        }
    },
    mounted() {
        this.primaryCalendarId = this.user.primary_calendar_id;
        this.autosync = this.user.cronofy_auto_sync;

        if (this.chooseCal) {
            this.selectCalendar();
        }

        let self = this;

        calendar.token('account_management').then(data => {
            this.calendarElement = CronofyElements.CalendarSync({
                element_token: data?.token,
                target_id: 'cronofy-calendar-sync',
                single_profile: 1,
                authorization: {
                    redirect_uri: route('app.oauth.cronofy.callback'),
                    client_id: data.client_id,
                    scope: 'read_write',
                },
                styles: {
                    prefix: 'CalendarSync',
                },
                data_center: 'de',
                locale: this.getLocale(),
                callback(data) {
                    if (data.notification.type === 'profile_revoked') {
                        // Profile has been deleted.
                        calendar.destroy(data.notification.profile.profile_id)
                            .then(async result => {
                                return useRepo(CalendarProfile)
                                    .where('profile_id', data.notification.profile.profile_id)
                                    .delete();
                            })
                            .then(() => {
                                // update User model in store
                                useRepo(User)
                                    .whereId(self.user.id)
                                    .update({
                                        cronofy_auto_sync: true,
                                        calendar_profiles: self.user.calendar_profiles.filter(i => i.profile_id !== data.notification.profile.profile_id),
                                        primary_calendar_id: null
                                    });

                                // When revoking calendar also remove linked availability
                                calendar.deleteAvailabilityRules().then(result => {
                                    useRepo(User)
                                        .whereId(self.user.id)
                                        .update({ cronofy_auto_sync: true, availability_rules: [] });
                                });
                            });
                    }
                },
            });
            this.loading = false;
        });
    },
};
</script>
<style>
.CalendarSync__title {
    @apply !font-semibold text-black text-body;
}

.CalendarSync__edit-toggle-text {
    @apply !font-semibold text-neutral-grey text-body-sm;
}
</style>
